import { useCallback, useEffect, useState } from 'react';
import { CASH_DIFFERENCES_PAGE_SIZE } from '../../infra/cashDifferencesConstants';
import type { CashDifferenceRow } from '../../infra/cashSummaryQuery';
import { resolveCashDifferencesPage } from '../../infra/cashDifferencesResolve';
import {
  subscribeCashSessionChangeSignalDebounced,
} from '../../infra/cashSessionChangeSignal';
import { captureCatch } from '../../../shared/sentry/sentryUser';

type Params = {
  currentBranchId: string | null | undefined;
};

export function useCashDifferences({ currentBranchId }: Params) {
  const [differencesSummary, setDifferencesSummary] = useState<CashDifferenceRow[]>([]);

  const loadDifferencesFirstPage = useCallback(async () => {
    if (!currentBranchId) return;
    try {
      const page = await resolveCashDifferencesPage(currentBranchId, {
        lastDays: 7,
        limit: CASH_DIFFERENCES_PAGE_SIZE,
        offset: 0,
      });
      setDifferencesSummary(page);
    } catch (e) {
      captureCatch('cash', 'useCashDifferences.load')(e);
    }
  }, [currentBranchId]);

  const loadFn = useCallback(async () => {
    if (!currentBranchId) {
      setDifferencesSummary([]);
      return;
    }
    await loadDifferencesFirstPage();
  }, [currentBranchId, loadDifferencesFirstPage]);

  useEffect(() => {
    return subscribeCashSessionChangeSignalDebounced(() => {
      void loadDifferencesFirstPage();
    });
  }, [loadDifferencesFirstPage]);

  return {
    differencesSummary,
    loadDifferencesFirstPage,
    loadFn,
  };
}
